import {
  IconBuildingStore,
  IconCash,
  IconChevronLeft,
  IconChevronRight,
  IconFileInvoice,
  IconGavel,
  IconLayoutDashboard,
  IconPackages,
  IconReceipt2,
  IconScale,
  IconSettings,
  IconShoppingCart,
  IconTruckDelivery,
  type Icon,
} from "@tabler/icons-react";
import { NavLink, useLocation } from "react-router";
import { useT } from "../../i18n/provider";
import { activeItem, visibleNavigation } from "../../app/navigation";
import { useCapabilities } from "../../lib/session";
import { cx, ICON_STROKE } from "../../lib/cx";
import { useVocabulary } from "../../i18n/vocabulary";

/**
 * The navigation pane — docs/24 §5. A `NavigationView` in COMPACT mode.
 *
 * 224px wide with 32px items, Windows' standard control height. Collapsed it is
 * 48px, Windows' stated rail width.
 *
 * The selected item draws the **selection pill**: a 3px rounded accent bar on its
 * leading edge. The pill is a SHAPE, so it survives a contrast theme where a
 * tinted background does not. The selection is NOT re-weighted; the pill, the
 * fill and `aria-current="page"` are the three cues, two of which are not colour.
 */

const GLYPHS: Record<string, Icon> = {
  "/": IconLayoutDashboard,
  "/merchants": IconBuildingStore,
  "/catalog": IconPackages,
  "/orders": IconShoppingCart,
  "/disputes": IconGavel,
  "/dispatch": IconTruckDelivery,
  "/finance/payables": IconCash,
  "/finance/trial-balance": IconScale,
  "/tax/issuers": IconReceipt2,
  "/settings": IconSettings,
};

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { i18n, locale } = useT();
  const vocab = useVocabulary();
  const capabilities = useCapabilities();
  const { pathname } = useLocation();
  const current = activeItem(pathname);
  const groups = visibleNavigation(capabilities);

  return (
    <nav
      aria-label={i18n._("Sections")}
      dir="ltr"
      lang={locale}
      className={cx(
        "no-print flex shrink-0 flex-col gap-1 overflow-y-auto border-r border-border bg-background py-2 transition-[width] duration-[var(--duration-normal)] ease-[var(--ease-point)]",
        collapsed ? "w-12 px-1" : "w-56 px-2",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? i18n._("Expand the navigation") : i18n._("Collapse the navigation")}
        aria-expanded={!collapsed}
        className="mb-1 flex h-8 w-full items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-control-hover hover:text-foreground"
      >
        {collapsed ? (
          <IconChevronRight size={16} stroke={ICON_STROKE} aria-hidden />
        ) : (
          <IconChevronLeft size={16} stroke={ICON_STROKE} aria-hidden />
        )}
      </button>

      {groups.map((group) => (
        <div key={group.id} className="flex flex-col">
          {/* A group heading is a caption, and it disappears on the rail rather
              than being abbreviated into two letters nobody can read. */}
          {!collapsed && (
            <h2 className="mt-2 mb-1 px-2 text-xs text-muted-foreground">{vocab.nav(group.label)}</h2>
          )}
          {group.items.map((item) => {
            const Glyph = GLYPHS[item.path] ?? IconFileInvoice;
            const selected = current?.path === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? vocab.nav(item.label) : undefined}
                aria-current={selected ? "page" : undefined}
                className={cx(
                  "selection-pill relative flex h-8 items-center gap-3 rounded-md px-2 text-sm outline-none",
                  "before:absolute before:top-1.5 before:bottom-1.5 before:left-0 before:w-[3px] before:rounded-full before:content-['']",
                  selected
                    ? "bg-control-hover text-foreground before:bg-primary"
                    : "text-muted-foreground before:bg-transparent hover:bg-control-hover hover:text-foreground",
                )}
              >
                <Glyph size={16} stroke={ICON_STROKE} aria-hidden className="shrink-0" />
                {/* Sized to the longer of the two languages, and truncation is
                    the last resort rather than the layout: the pane is 224px
                    because that is what the widest Mongolian label needs. */}
                {!collapsed && <span className="truncate">{vocab.nav(item.label)}</span>}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
